import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Upload, Link2, FileAudio, Loader2, CheckCircle2, XCircle, Trash2, Plus, Sparkles, ExternalLink } from "lucide-react";
import MeetingNotesDialog from "@/components/MeetingNotesDialog";

const statusMeta = {
  processing: { label: "Transcribing…", cls: "bg-blue-100 text-blue-700", Icon: Loader2, spin: true },
  transcribed: { label: "Transcribed", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700", Icon: XCircle },
  link_only: { label: "Link saved", cls: "bg-zinc-100 text-zinc-600", Icon: Link2 },
};

const StatusPill = ({ status }) => {
  const m = statusMeta[status] || statusMeta.link_only;
  const { Icon } = m;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full text-xs px-2.5 py-0.5 font-[Manrope] font-medium ${m.cls}`}>
      <Icon className={`h-3 w-3 ${m.spin ? "animate-spin" : ""}`} /> {m.label}
    </span>
  );
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export default function MeetingsTab({ client }) {
  const [meetings, setMeetings] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    api.get(`/clients/${client.client_id}/meetings`).then((r) => setMeetings(r.data)).catch(() => {});
  }, [client.client_id]);

  useEffect(() => {
    load();
    const iv = setInterval(() => {
      setMeetings((prev) => {
        if (prev.some((m) => m.status === "processing" || m.analysis_status === "processing")) load();
        return prev;
      });
    }, 4000);
    return () => clearInterval(iv);
  }, [load]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/meetings/${deleteTarget.meeting_id}`);
      toast.success("Meeting deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="meetings-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#193962]" />
          <h3 className="font-[Outfit] font-semibold tracking-tight">Recorded Meetings</h3>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-meeting-btn" className="bg-[#193962] hover:bg-[#12294A] text-white transition-all duration-200 hover:-translate-y-[1px]">
          <Plus className="h-4 w-4 mr-1.5" /> Add Recording
        </Button>
      </div>

      <div className="border border-border bg-white rounded-md divide-y divide-border" data-testid="meetings-list">
        {meetings.length === 0 ? (
          <div className="py-16 text-center">
            <FileAudio className="h-8 w-8 mx-auto text-zinc-300 mb-3" />
            <p className="font-[Manrope] text-muted-foreground">No recordings yet. Upload an audio/video file to auto-transcribe.</p>
          </div>
        ) : (
          meetings.map((m) => (
            <div key={m.meeting_id} className="px-5 py-4 flex items-center gap-4 hover:bg-zinc-50 transition-colors" data-testid={`meeting-row-${m.meeting_id}`}>
              <div className="h-10 w-10 rounded-md bg-[#193962]/5 text-[#193962] flex items-center justify-center shrink-0">
                {m.source_type === "link" ? <Link2 className="h-5 w-5" /> : <FileAudio className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-[Manrope] font-medium text-sm truncate">{m.title}</p>
                <p className="text-xs text-muted-foreground font-[Manrope]">
                  {fmtDate(m.meeting_date)}
                  {m.original_filename ? ` · ${m.original_filename}` : ""}
                </p>
              </div>
              <StatusPill status={m.status} />
              <div className="flex items-center gap-1 shrink-0">
                {m.status === "transcribed" && (
                  <Button size="sm" variant="outline" onClick={() => setViewingId(m.meeting_id)} data-testid={`view-transcript-${m.meeting_id}`}>
                    View Notes
                  </Button>
                )}
                {m.source_type === "link" && m.meet_link && (
                  <a href={m.meet_link} target="_blank" rel="noreferrer" className="h-8 w-8 flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-500 hover:text-[#193962]" data-testid={`open-link-${m.meeting_id}`}>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                <button onClick={() => setDeleteTarget(m)} data-testid={`delete-meeting-${m.meeting_id}`} className="h-8 w-8 flex items-center justify-center rounded hover:bg-red-50 text-zinc-500 hover:text-red-600 transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <AddMeetingDialog open={open} onOpenChange={setOpen} client={client} onAdded={load} />

      <MeetingNotesDialog meetingId={viewingId} onClose={() => { setViewingId(null); load(); }} onChanged={load} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-[Outfit]">Delete recording?</AlertDialogTitle>
            <AlertDialogDescription className="font-[Manrope]">
              This permanently removes <strong>{deleteTarget?.title}</strong> and its transcript.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-meeting" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AddMeetingDialog({ open, onOpenChange, client, onAdded }) {
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState("upload");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(today);
  const [link, setLink] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setTitle(""); setDate(today); setLink(""); setFile(null); setMode("upload"); }
    // eslint-disable-next-line
  }, [open]);

  const submit = async () => {
    if (!title.trim()) { toast.error("Add a title"); return; }
    if (mode === "upload" && !file) { toast.error("Choose a file"); return; }
    if (mode === "link" && !link.trim()) { toast.error("Paste a recording link"); return; }
    if (mode === "upload" && file && file.size > 25 * 1024 * 1024) { toast.error("File exceeds 25 MB"); return; }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("meeting_date", date);
      fd.append("source_type", mode);
      if (mode === "link") fd.append("meet_link", link);
      if (mode === "upload") fd.append("file", file);
      await api.post(`/clients/${client.client_id}/meetings`, fd);
      toast.success(mode === "upload" ? "Uploaded — transcription started" : "Recording link saved");
      onOpenChange(false);
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add recording");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="add-meeting-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-2xl tracking-tight">Add Meeting Recording</DialogTitle>
          <DialogDescription className="font-[Manrope]">Upload a file to auto-transcribe with AI, or save a Meet link for reference.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="font-[Manrope] text-sm">Title</Label>
            <Input data-testid="meeting-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Discovery call" className="mt-1.5" />
          </div>
          <div>
            <Label className="font-[Manrope] text-sm">Meeting Date</Label>
            <Input type="date" data-testid="meeting-date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" />
          </div>

          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="w-full">
              <TabsTrigger value="upload" data-testid="mode-upload" className="flex-1 font-[Manrope]"><Upload className="h-4 w-4 mr-1.5" /> Upload File</TabsTrigger>
              <TabsTrigger value="link" data-testid="mode-link" className="flex-1 font-[Manrope]"><Link2 className="h-4 w-4 mr-1.5" /> Meet Link</TabsTrigger>
            </TabsList>
            <TabsContent value="upload" className="mt-3">
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-md py-8 cursor-pointer hover:border-[#193962]/40 transition-colors" data-testid="file-dropzone">
                <Upload className="h-6 w-6 text-zinc-400 mb-2" />
                <span className="font-[Manrope] text-sm text-muted-foreground">{file ? file.name : "Click to choose mp3, mp4, wav, m4a (max 25 MB)"}</span>
                <input type="file" accept=".mp3,.mp4,.wav,.m4a,.mpeg,.mpga,.webm,audio/*,video/mp4" className="hidden" data-testid="meeting-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
            </TabsContent>
            <TabsContent value="link" className="mt-3">
              <Input data-testid="meeting-link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://drive.google.com/…/recording" />
              <p className="text-xs text-muted-foreground font-[Manrope] mt-2">Meet links are stored as a reference. Auto-transcription runs when Google Drive is connected.</p>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="submit-meeting-btn" className="bg-[#193962] hover:bg-[#12294A] text-white">
            {busy ? "Uploading…" : mode === "upload" ? "Upload & Transcribe" : "Save Link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
